import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { US_STATES } from "@/lib/listings";
import { toast } from "sonner";
import {
  Search, MapPin, Star, Clock, Compass, Tv, Moon, Sparkles,
  Globe, Navigation, Loader2, Share2, Trees, ExternalLink, Ticket,
  AlertCircle
} from "lucide-react";

const SECTIONS = [
  { value: "tourist",       icon: Compass, label_ar: "معالم سياحية", label_en: "Attractions", color: "from-yellow-500/30 to-amber-700/30" },
  { value: "entertainment", icon: Tv,      label_ar: "ترفيه",         label_en: "Entertainment", color: "from-purple-500/30 to-violet-700/30" },
  { value: "arabic",        icon: Moon,    label_ar: "أماكن عربية",   label_en: "Arabic Spots", color: "from-emerald-500/30 to-teal-700/30" },
  { value: "parks",         icon: Trees,   label_ar: "حدائق وطنية",   label_en: "National Parks", color: "from-green-500/30 to-emerald-700/30" },
];

// Categories per section — drive the Google Places query refinement.
const SECTION_CATEGORIES = {
  tourist: [
    { v: "", ar: "كل المعالم", en: "All attractions" },
    { v: "landmark", ar: "معالم", en: "Landmarks" },
  ],
  entertainment: [
    { v: "", ar: "كل الترفيه", en: "All entertainment" },
    { v: "mall", ar: "مولات", en: "Malls" },
    { v: "outlet_mall", ar: "أوتلت مولات", en: "Outlet malls" },
    { v: "theme_park", ar: "ثيم باركس", en: "Theme parks" },
    { v: "amusement", ar: "ملاهي", en: "Amusement parks" },
    { v: "park", ar: "حدائق", en: "Parks" },
    { v: "beach", ar: "شواطئ", en: "Beaches" },
    { v: "zoo", ar: "حدائق حيوان", en: "Zoos" },
    { v: "aquarium", ar: "أحواض سمك", en: "Aquariums" },
    { v: "cinema", ar: "سينما", en: "Cinemas" },
    { v: "bowling", ar: "بولينج", en: "Bowling" },
    { v: "arcade", ar: "أركيد", en: "Arcades" },
    { v: "family_center", ar: "مراكز عائلية", en: "Family centers" },
    { v: "walking", ar: "ممرات مشي", en: "Walking trails" },
    { v: "kid_friendly", ar: "أماكن للأطفال", en: "Kid-friendly" },
  ],
  arabic: [
    { v: "", ar: "كل الأماكن العربية", en: "All Arabic places" },
    { v: "halal_food", ar: "مطاعم حلال", en: "Halal restaurants" },
    { v: "arabic_food", ar: "مطاعم عربية", en: "Arabic restaurants" },
    { v: "mosque", ar: "مساجد", en: "Mosques" },
    { v: "supermarket", ar: "بقالة شرق أوسطية", en: "Middle Eastern grocery" },
    { v: "arab_bakery", ar: "مخابز عربية", en: "Arab bakeries" },
    { v: "arab_community", ar: "مراكز جالية", en: "Community centers" },
  ],
  parks: [
    { v: "", ar: "كل الحدائق الوطنية", en: "All national parks" },
  ],
};

const shareItem = (p, t) => {
  const url = p.website || p.address || window.location.href;
  if (navigator.share) {
    navigator.share({ title: p.name, text: p.description?.slice(0, 100), url }).catch(() => {});
  } else {
    navigator.clipboard?.writeText(url);
    toast.success(t("تم نسخ الرابط", "Link copied"));
  }
};

const PlaceCard = ({ p, t, lang }) => {
  const isPark = p.source === "nps";
  return (
    <div
      className="text-right group glass-card rounded-2xl overflow-hidden hover:border-yellow-500/60 transition-all purple-gold-glow"
      data-testid={`place-card-${p.place_id}`}
    >
      <div className="relative h-52 overflow-hidden bg-slate-900">
        {p.image ? (
          <img
            src={p.image}
            alt={p.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
            {isPark ? <Trees size={48} className="text-emerald-500/40" /> : <Compass size={48} className="text-yellow-500/40" />}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

        {isPark && (
          <Badge className="absolute top-3 right-3 bg-gradient-to-r from-emerald-500 to-green-700 text-white border-0">
            <Trees size={12} className="ml-1" /> {t("حديقة وطنية", "National Park")}
          </Badge>
        )}

        {p.distance_miles !== undefined && p.distance_miles !== null && (
          <div className="absolute top-3 left-3 bg-black/70 backdrop-blur-md text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
            <Navigation size={12} className="text-yellow-500" /> {p.distance_miles} mi
          </div>
        )}

        <div className="absolute bottom-3 right-3 left-3 flex items-end justify-between">
          <div className="text-white">
            <h3 className="font-bold text-lg leading-tight drop-shadow-lg line-clamp-2">{p.name}</h3>
            {(p.city || p.state) && (
              <p className="text-xs text-slate-300 mt-1 flex items-center gap-1">
                <MapPin size={12} className="text-yellow-500" /> {[p.city, p.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
          {p.rating !== undefined && p.rating !== null && (
            <div className="bg-yellow-500/90 text-black text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
              <Star size={12} fill="currentColor" /> {p.rating}
              {p.ratings_count > 0 && <span className="opacity-70">({p.ratings_count > 1000 ? `${Math.floor(p.ratings_count / 1000)}k` : p.ratings_count})</span>}
            </div>
          )}
        </div>
      </div>

      <div className="p-4 space-y-2">
        {p.description && <p className="text-sm text-slate-400 line-clamp-2 text-right">{p.description}</p>}

        {/* NPS-specific row: fee + hours */}
        {isPark && (p.fee || p.opening_hours) && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 pt-1">
            {p.fee && <span className="flex items-center gap-1"><Ticket size={12} className="text-yellow-500" />{p.fee}</span>}
            {p.opening_hours && <span className="flex items-center gap-1"><Clock size={12} className="text-purple-400" />{p.opening_hours}</span>}
          </div>
        )}

        {/* NPS activities */}
        {isPark && p.activities && p.activities.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1" data-testid="park-activities">
            {p.activities.slice(0, 5).map((a) => (
              <Badge key={a} className="bg-emerald-500/10 text-emerald-300 text-[10px]">{a}</Badge>
            ))}
            {p.activities.length > 5 && <span className="text-[10px] text-slate-500">+{p.activities.length - 5}</span>}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-slate-800">
          {p.website ? (
            <a href={p.website} target="_blank" rel="noreferrer" className="flex-1">
              <Button className="w-full btn-primary" size="sm" data-testid={`place-website-${p.place_id}`}>
                <ExternalLink size={12} className="ml-1" />{t("الموقع الرسمي", "Official site")}
              </Button>
            </a>
          ) : p.address ? (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1"
            >
              <Button className="w-full btn-primary" size="sm" data-testid={`place-directions-${p.place_id}`}>
                <Navigation size={12} className="ml-1" />{t("الاتجاهات", "Directions")}
              </Button>
            </a>
          ) : <div className="flex-1" />}
          <button
            onClick={() => shareItem(p, t)}
            className="px-3 text-xs py-2 rounded-md bg-slate-800 text-slate-300 hover:bg-purple-500/20 hover:text-purple-300 flex items-center justify-center gap-1 transition-colors"
            data-testid={`place-share-${p.place_id}`}
          >
            <Share2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function TourismDiscovery() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [section, setSection] = useState("tourist");
  const [filters, setFilters] = useState({ state: "", city: "", zip_code: "", category: "" });
  const [places, setPlaces] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [origin, setOrigin] = useState(null);  // { lat, lng } when "Near Me" used
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [currentKeyword, setCurrentKeyword] = useState(null);

  const fetchPage = useCallback(async ({ append = false, token = null } = {}) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = { section };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (origin) { params.lat = origin.lat; params.lng = origin.lng; }
      if (token) params.next_token = token;
      const res = await axios.get(`${API}/discover/places`, { params });
      setCurrentKeyword(res.data.current_keyword || null);
      setNextToken(res.data.next_token || null);
      setPlaces((prev) => append ? [...prev, ...res.data.results] : res.data.results);
    } catch (e) {
      console.error(e);
      if (!append) setPlaces([]);
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [section, filters, origin]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const useMyLocation = () => {
    setLocationError(null);
    if (!navigator.geolocation) {
      setLocationError("no_api");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
        toast.success(t("سنعرض أقرب الأماكن إليك الآن", "Showing places nearest to you"));
      },
      (err) => {
        setLocating(false);
        setLocationError(err.code === 1 ? "denied" : "unknown");
      },
      { timeout: 8000, maximumAge: 60_000 }
    );
  };

  const clearLocation = () => { setOrigin(null); setLocationError(null); };

  const categories = SECTION_CATEGORIES[section] || [];
  const isParks = section === "parks";

  // ─── Always-on featured destinations — verified Wikipedia/Wikimedia canonical images ───
  // Each URL is the canonical "page image" of the corresponding Wikipedia article,
  // ensuring photographic accuracy (e.g., Grand Canyon shows the Grand Canyon, not Antelope Canyon).
  const FEATURED_DESTINATIONS = [
    { id: "statue-liberty", name_ar: "تمثال الحرية",      name_en: "Statue of Liberty",   city: "New York, NY",        img: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/89/Front_view_of_Statue_of_Liberty_%28cropped%29.jpg/960px-Front_view_of_Statue_of_Liberty_%28cropped%29.jpg", group: "popular" },
    { id: "times-square",   name_ar: "تايمز سكوير",       name_en: "Times Square",        city: "New York, NY",        img: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/New_york_times_square-terabass.jpg/960px-New_york_times_square-terabass.jpg", group: "popular" },
    { id: "central-park",   name_ar: "سنترال بارك",        name_en: "Central Park",        city: "New York, NY",        img: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f1/Global_Citizen_Festival_Central_Park_New_York_City_from_NYonAir_%2815351915006%29.jpg/960px-Global_Citizen_Festival_Central_Park_New_York_City_from_NYonAir_%2815351915006%29.jpg", group: "popular" },
    { id: "niagara",        name_ar: "شلالات نياجرا",      name_en: "Niagara Falls",       city: "New York / Ontario",  img: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ab/3Falls_Niagara.jpg/960px-3Falls_Niagara.jpg", group: "parks" },
    { id: "grand-canyon",   name_ar: "غراند كانيون",       name_en: "Grand Canyon",        city: "Arizona",             img: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Canyon_River_Tree_%28165872763%29.jpeg/960px-Canyon_River_Tree_%28165872763%29.jpeg", group: "parks" },
    { id: "yellowstone",    name_ar: "يلوستون",            name_en: "Yellowstone",         city: "Wyoming",             img: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Grand_Canyon_of_yellowstone.jpg/960px-Grand_Canyon_of_yellowstone.jpg", group: "parks" },
    { id: "miami-beach",    name_ar: "ميامي بيتش",         name_en: "Miami Beach",         city: "Miami, FL",           img: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/Ocean_drive_day_2009j.JPG/960px-Ocean_drive_day_2009j.JPG", group: "entertainment" },
    { id: "universal",      name_ar: "يونيفرسال ستوديوز",  name_en: "Universal Studios",   city: "Orlando, FL",         img: "https://upload.wikimedia.org/wikipedia/commons/thumb/6/61/Universal_archway_2019.jpg/960px-Universal_archway_2019.jpg", group: "entertainment" },
    { id: "hollywood",      name_ar: "لافتة هوليوود",       name_en: "Hollywood Sign",      city: "Los Angeles, CA",     img: "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2f/Hollywood_sign_%288485145044%29.jpg/960px-Hollywood_sign_%288485145044%29.jpg", group: "entertainment" },
    { id: "disney-world",   name_ar: "ديزني وورلد",        name_en: "Walt Disney World",   city: "Orlando, FL",         img: "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/Cinderella_Castle%2C_Magic_Kingdom_%282026%29_%28square_crop%29.jpg/960px-Cinderella_Castle%2C_Magic_Kingdom_%282026%29_%28square_crop%29.jpg", group: "entertainment" },
  ];
  const GROUPS = [
    { key: "popular",       ar: "🗽 مدن مشهورة",          en: "🗽 Popular Cities" },
    { key: "parks",         ar: "🏞️ حدائق وطنية",         en: "🏞️ National Parks" },
    { key: "arab",          ar: "🌙 أماكن صديقة للعرب",   en: "🌙 Arab-Friendly Places" },
    { key: "entertainment", ar: "🎬 ترفيه وعائلات",        en: "🎬 Entertainment" },
  ];
  const ARAB_FRIENDLY = [
    { id: "dearborn",   name_ar: "ديربورن (الجالية العربية)", name_en: "Dearborn (Arab community)", city: "Michigan",        img: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Former_city_hall_during_Memorial_Day_Parade%2C_Dearborn%2C_Michigan.jpg/960px-Former_city_hall_during_Memorial_Day_Parade%2C_Dearborn%2C_Michigan.jpg", group: "arab" },
    { id: "patterson",  name_ar: "باترسون نيو جيرسي",         name_en: "Paterson, NJ",              city: "New Jersey",      img: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/2024-08-13_11_25_40_View_north-northeast_towards_downtown_Paterson_from_the_main_overlook_within_the_Garret_Mountain_Reservation_in_Paterson%2C_Passaic_County%2C_New_Jersey.jpg/960px-thumbnail.jpg", group: "arab" },
    { id: "anaheim",    name_ar: "أنهايم (ليتل عرب)",         name_en: "Anaheim (Little Arabia)",   city: "California",      img: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/Sleeping_beauty_castle_dlr_2019.jpg/960px-Sleeping_beauty_castle_dlr_2019.jpg", group: "arab" },
  ];
  const ALL_FEATURED = [...FEATURED_DESTINATIONS, ...ARAB_FRIENDLY];
  // Universal fallback for any broken destination image — neutral US scenery (Unsplash CDN).
  const FALLBACK_IMG = "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?w=800&h=520&fit=crop&q=85";

  return (
    <DashboardLayout title={t("اكتشف أمريكا", "Discover America")}>
      <div className="space-y-5 lg:space-y-7" data-testid="tourism-discovery">
        {/* Featured destinations — always visible, no filter required */}
        {GROUPS.map((g) => {
          const items = ALL_FEATURED.filter((x) => x.group === g.key);
          if (items.length === 0) return null;
          return (
            <section key={g.key} data-testid={`featured-${g.key}`}>
              <h3 className="font-['Cairo'] font-black text-lg sm:text-xl mb-2.5" style={{ color: "var(--text-primary)" }}>{t(g.ar, g.en)}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                {items.map((p) => (
                  <button key={p.id}
                    onClick={() => { setSection(g.key === "parks" ? "parks" : g.key === "arab" ? "arabic" : g.key === "entertainment" ? "entertainment" : "tourist"); }}
                    className="card-soft overflow-hidden text-right group"
                    style={{ background: "var(--bg-surface)" }}
                    data-testid={`featured-card-${p.id}`}>
                    <div className="relative h-28 sm:h-36 overflow-hidden">
                      <img
                        src={p.img}
                        alt={t(p.name_ar, p.name_en)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => { if (e.target.src !== FALLBACK_IMG) e.target.src = FALLBACK_IMG; }}
                      />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,11,26,0.92) 0%, transparent 60%)" }} />
                      <div className="absolute bottom-2 right-2.5 text-white">
                        <div className="font-['Cairo'] font-bold text-xs sm:text-sm drop-shadow leading-tight">{t(p.name_ar, p.name_en)}</div>
                        <div className="text-[10px] opacity-90 drop-shadow inline-flex items-center gap-0.5"><MapPin size={9} /> {p.city}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </section>
          );
        })}
        {/* Hero — unchanged styling */}
        <div className="relative glass-card rounded-3xl overflow-hidden p-6 lg:p-10 bg-luxury">
          <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-yellow-500/20 blur-[140px] rounded-full" />
          <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] bg-purple-500/20 blur-[140px] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Badge className="bg-gradient-to-r from-purple-500/20 to-yellow-500/20 text-yellow-300 mb-3 border-yellow-500/30">
                <Sparkles size={12} className="ml-1" /> {t("اكتشف الأماكن", "Discover Places")}
              </Badge>
              <h2 className="text-3xl lg:text-5xl font-bold mb-2 leading-tight text-luxury">
                {t("أمريكا كما لم ترها من قبل", "America like never before")}
              </h2>
              <p className="text-slate-400 max-w-2xl">
                {t(
                  "معالم سياحية، ترفيه، أماكن عربية، وحدائق وطنية — مصادر حيّة من Google Places و National Park Service.",
                  "Live attractions, entertainment, Arabic spots, and National Parks — powered by Google Places and the National Park Service.",
                )}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={() => navigate("/favorites")} variant="outline" className="border-yellow-500/40 text-yellow-400 hover:bg-yellow-500/10" data-testid="goto-favorites-btn">
                <Star className="ml-2" size={18} /> {t("المفضلة", "Favorites")}
              </Button>
              <Button onClick={useMyLocation} disabled={locating} className="btn-primary" data-testid="use-my-location-btn">
                {locating ? <Loader2 className="animate-spin ml-2" size={18} /> : <Navigation className="ml-2" size={18} />}
                {origin ? t("أعد البحث من موقعي", "Re-search from my location") : t("الأماكن قريبة مني", "Places near me")}
              </Button>
              {origin && (
                <Button onClick={clearLocation} variant="outline"
                  className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="clear-location-btn">
                  {t("إلغاء", "Clear")}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Location error notice — friendly Arabic message */}
        {locationError && (
          <div className="glass-card rounded-2xl p-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3" data-testid="location-error">
            <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
            <div className="flex-1">
              <p className="text-amber-200 font-semibold mb-1">
                {locationError === "denied"
                  ? t("لم نتمكن من الوصول إلى موقعك", "We couldn't access your location")
                  : t("الموقع الجغرافي غير متاح", "Location not available")}
              </p>
              <p className="text-sm text-amber-100/80">
                {t(
                  "لا مشكلة — استخدم البحث بالولاية أو المدينة أو الرمز البريدي (ZIP) في الأسفل لمشاهدة الأماكن في منطقتك.",
                  "No problem — search by state, city, or ZIP code below to find places in your area.",
                )}
              </p>
            </div>
            <button onClick={() => setLocationError(null)} className="text-amber-300 hover:text-amber-100 text-xl leading-none px-1">×</button>
          </div>
        )}

        {/* Origin badge when active */}
        {origin && (
          <div className="glass-card rounded-2xl p-3 border border-yellow-500/30 bg-yellow-500/5 flex items-center gap-3" data-testid="origin-active">
            <Navigation className="text-yellow-400" size={16} />
            <p className="text-sm text-yellow-100/90">
              {t(`نعرض الأماكن قرب: ${origin.lat.toFixed(3)}, ${origin.lng.toFixed(3)}`,
                 `Showing places near: ${origin.lat.toFixed(3)}, ${origin.lng.toFixed(3)}`)}
            </p>
          </div>
        )}

        {/* Section tabs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="discover-sections">
          {SECTIONS.map((s) => (
            <button
              key={s.value}
              onClick={() => { setSection(s.value); setFilters({ ...filters, category: "" }); setNextToken(null); }}
              className={`relative overflow-hidden rounded-2xl px-4 py-5 text-right border transition-all ${
                section === s.value
                  ? "border-yellow-500 bg-yellow-500/10"
                  : "border-slate-700 bg-slate-900 hover:border-slate-600"
              }`}
              data-testid={`section-tab-${s.value}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-50`} />
              <div className="relative flex items-center gap-3 justify-end">
                <div>
                  <h4 className={`font-bold text-lg ${section === s.value ? "text-yellow-400" : "text-white"}`}>
                    {language === "ar" ? s.label_ar : s.label_en}
                  </h4>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${section === s.value ? "bg-yellow-500 text-black" : "bg-slate-800 text-yellow-500"}`}>
                  <s.icon size={20} />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Search bar */}
        <div className="glass-card rounded-2xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="discover-filters">
          <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="tour-filter-state">
            <option value="">{t("كل الولايات", "All States")}</option>
            {US_STATES.map((s) => <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>)}
          </select>
          {!isParks && (
            <input placeholder={t("المدينة", "City")} value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="tour-filter-city" />
          )}
          {!isParks && (
            <input placeholder={t("الرمز البريدي", "ZIP code")} value={filters.zip_code}
              onChange={(e) => setFilters({ ...filters, zip_code: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="tour-filter-zip" />
          )}
          <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white" data-testid="tour-filter-category">
            {categories.map((c) => <option key={c.v} value={c.v}>{language === "ar" ? c.ar : c.en}</option>)}
          </select>
          <Button onClick={() => { setNextToken(null); fetchPage(); }} className="btn-primary" data-testid="tour-search-btn">
            <Search size={16} className="ml-2" /> {t("بحث", "Search")}
          </Button>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Globe className="text-purple-400" size={20} />
              {t("النتائج", "Results")} <span className="text-sm text-slate-400 font-normal">({places.length})</span>
            </h3>
            {currentKeyword && (
              <Badge className="bg-slate-800 text-slate-300 text-xs">
                {t("الفئة الحالية:", "Current:")} {currentKeyword}
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1,2,3,4,5,6].map((i) => <div key={i} className="glass-card rounded-2xl h-80 skeleton" />)}
            </div>
          ) : places.length === 0 ? (
            <div className="glass-card rounded-2xl p-12 text-center text-slate-400" data-testid="discover-empty">
              {t("لا توجد أماكن تطابق البحث — جرّب توسيع الفلاتر.", "No places match your search — try widening your filters.")}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {places.map((p) => <PlaceCard key={p.place_id} p={p} t={t} lang={language} />)}
            </div>
          )}

          {/* Load More */}
          {nextToken && !loading && (
            <div className="flex justify-center pt-6">
              <Button
                onClick={() => fetchPage({ append: true, token: nextToken })}
                disabled={loadingMore}
                className="btn-primary px-8"
                data-testid="discover-load-more"
              >
                {loadingMore
                  ? <><Loader2 className="animate-spin ml-2" size={16} />{t("جارٍ التحميل…", "Loading…")}</>
                  : <>{t("عرض المزيد", "Load more")}</>
                }
              </Button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
